import React from "react"
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material"
import { ViraText } from "../atoms/ViraText"
import { ContentCard } from "./ContentCard"
import { composeSx } from "../theme/composeSx"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type TheirWeekDay = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun"

export type TheirWeekEntry = {
  id: string
  day: TheirWeekDay
  label: string
  /** Solid = added; dashed = proposed / not yet added */
  proposed?: boolean
}

export type TheirWeekProps = {
  title?: string
  subtitle?: string
  legend?: string
  entries?: TheirWeekEntry[]
  /**
   * Below `lg`, render compact (same ContentCard; layout still stacks).
   */
  collapsible?: boolean
  /** Ignored — kept for call-site compat. */
  defaultExpanded?: boolean
  sx?: React.ComponentProps<typeof Box>["sx"]
}

const DAYS: TheirWeekDay[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

function WeekBody({
  legend,
  entries,
}: {
  legend: string
  entries: TheirWeekEntry[]
}) {
  return (
    <>
      <ViraText tone="muted" sx={{ fontSize: 12, mb: 1.5 }}>
        {legend}
      </ViraText>
      <Stack spacing={1.25}>
        {DAYS.map((day) => {
          const dayEntries = entries.filter((entry) => entry.day === day)
          return (
            <Box key={day}>
              <ViraText
                tone="muted"
                sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}
              >
                {day}
              </ViraText>
              {dayEntries.length === 0 ? (
                <Box
                  sx={(theme) => ({
                    height: 4,
                    borderRadius: 2,
                    bgcolor: resolveViraPalette(theme).softSurface,
                  })}
                />
              ) : (
                <Stack spacing={0.5}>
                  {dayEntries.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={(theme) => ({
                        px: 1,
                        py: 0.75,
                        borderRadius: "6px",
                        fontSize: 12,
                        fontFamily: '"Lato", sans-serif',
                        color: resolveViraPalette(theme).ink,
                        bgcolor: entry.proposed
                          ? theme.palette.background.paper
                          : resolveViraPalette(theme).softSurface,
                        border: entry.proposed
                          ? `1px dashed ${resolveViraPalette(theme).border}`
                          : `1px solid ${resolveViraPalette(theme).softBorder}`,
                        maxWidth: "100%",
                        overflowWrap: "anywhere",
                        whiteSpace: "normal",
                      })}
                    >
                      {entry.label}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )
        })}
      </Stack>
    </>
  )
}

export function TheirWeek({
  title = "Their week",
  subtitle,
  legend = "dashed = proposed",
  entries = [],
  sx,
}: TheirWeekProps) {
  const theme = useTheme()
  const isNarrow = useMediaQuery(theme.breakpoints.down("lg"))

  const shellSx = composeSx(
    () => ({
      width: "100%",
      maxWidth: { xs: "100%", lg: 280 },
    }),
    sx,
  )

  return (
    <ContentCard
      sx={shellSx}
      title={title}
      meta={
        subtitle ? (
          <ViraText tone="subtle" sx={{ fontSize: 12 }}>
            {subtitle}
            {isNarrow ? "" : ""}
          </ViraText>
        ) : undefined
      }
    >
      <WeekBody legend={legend} entries={entries} />
    </ContentCard>
  )
}
